import { useState, useMemo } from "react";
import { useQuery, useMutation } from "convex/react";
import { useNavigate } from "react-router";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { useSettings } from "@/hooks/use-settings";
import { calculateFuelCost } from "@/lib/fuel-calculations";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { formatTimeWithPreference } from "@/lib/format";
import { useTimeFormat } from "@/lib/time-format-context";
import { format, parseISO } from "date-fns";
import {
  Pencil,
  Trash2,
  FileText,
  Share2,
  Mail,
  PartyPopper,
  X,
  Banknote,
} from "lucide-react";
import { DeleteDialog } from "./delete-dialog";
import { BlockFormSheet } from "./block-form-sheet";
import { IssuePhotoGallery } from "./issue-photo-gallery";
import { RouteRating } from "./route-rating";
import { BlockStatsBar } from "./block-stats-bar";
import { BlockEarningsCard } from "./block-earnings-card";
import { RouteSection } from "./route-section";

interface BlockDetailPageProps {
  blockId: Id<"blocks">;
}

export function BlockDetailPage({ blockId }: BlockDetailPageProps) {
  const navigate = useNavigate();
  const timeFormat = useTimeFormat();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const updateOverbooked = useMutation(api.blocks.updateOverbooked);

  const data = useQuery(
    api.blocks.getBlockWithShift,
    blockId ? { id: blockId } : "skip"
  );

  const screenshotUrl = useQuery(
    api.files.getFileUrl,
    data?.routeScreenshotId ? { storageId: data.routeScreenshotId } : "skip"
  );

  // Settings and fuel price for net profit calculation
  const { settings } = useSettings();
  const latestFuelPrice = useQuery(api.expenses.getLatestFuelPrice);

  // Calculate derived values (must be before early returns for hooks rules)
  const calculations = useMemo(() => {
    if (!data) return null;

    const { shift, ...block } = data;

    const totalEarnings = block.basePay + block.tip + block.noRoutePayout;

    const [startH, startM] = block.shiftStart.split(":").map(Number);
    const [endH, endM] = block.shiftEnd.split(":").map(Number);
    const durationMins = endH * 60 + endM - (startH * 60 + startM);

    // Mileage: prefer tracked miles if > 0, otherwise fallback to manual
    const shiftMiles = shift?.totalMiles;
    const miles =
      shiftMiles && shiftMiles > 0 ? shiftMiles : (block.manualMiles ?? null);

    return { totalEarnings, durationMins, miles, shift, block };
  }, [data]);

  // Fuel estimate calculation (must be before early returns for hooks rules)
  const fuelEstimate = useMemo(() => {
    if (!calculations?.miles || calculations.miles <= 0 || !settings?.mpg || !latestFuelPrice) return null;
    const cost = calculateFuelCost(calculations.miles, settings.mpg, latestFuelPrice);
    if (cost <= 0) return null;
    return {
      cost,
      mpg: settings.mpg,
      fuelPrice: latestFuelPrice,
    };
  }, [calculations?.miles, settings?.mpg, latestFuelPrice]);

  // Loading state
  if (data === undefined) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl space-y-4 pb-40">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  // Not found state
  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Block not found</p>
          <Button variant="outline" onClick={() => navigate("/blocks")}>
            Back to History
          </Button>
        </div>
      </div>
    );
  }

  // Extract from calculations (safe after null check above)
  const { shift, block, totalEarnings, durationMins, miles } = calculations!;

  // Handle email support
  const handleEmailSupport = () => {
    const stationPart = block.station ? `${block.station} ` : "";
    const subject = `Support Request - ${stationPart}${block.date} @ ${formatTimeWithPreference(block.shiftStart, timeFormat)}`;
    navigate(
      `/email/compose?blockId=${block._id}&subject=${encodeURIComponent(subject)}`
    );
  };

  // Additional calculations
  const formattedDate = format(parseISO(block.date), "EEEE, MMMM d");
  const hourlyRate = durationMins > 0 ? totalEarnings / (durationMins / 60) : 0;

  // Calculate actual rate: prefer manual actualMinutes, then shift timing
  let actualHourlyRate: number | null = null;
  if (block.actualMinutes && block.actualMinutes > 0) {
    actualHourlyRate = totalEarnings / (block.actualMinutes / 60);
  } else if (shift?.endedAt && shift?.startedAt) {
    const actualMs = shift.endedAt - shift.startedAt - (shift.pauseDuration || 0);
    const actualHours = actualMs / 60000 / 60;
    if (actualHours > 0) {
      actualHourlyRate = totalEarnings / actualHours;
    }
  }

  // Mileage source
  const shiftMiles = shift?.totalMiles;
  const perMileRate = miles && miles > 0 ? totalEarnings / miles : null;
  const mileageSource =
    shiftMiles && shiftMiles > 0
      ? "tracked"
      : block.manualMiles
        ? "manual"
        : null;

  // Status badge (only show for non-tracked states since "Tracked" shows by the map)
  const getStatusBadge = () => {
    if (shift?.status === "active")
      return <Badge variant="default">In Progress</Badge>;
    if (shift?.status === "paused")
      return <Badge variant="secondary">Paused</Badge>;
    if (shift) return null; // "Tracked" badge removed - redundant with map section
    if (block.manualMiles) return <Badge variant="outline">Manual</Badge>;
    return <Badge variant="destructive">No Mileage</Badge>;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl space-y-5 pb-24">
      {/* Header - Line 1 */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold">{formattedDate}</h1>
          {blockId && (
            <RouteRating blockId={blockId} rating={block.routeRating} size="sm" />
          )}
        </div>
{getStatusBadge()}
      </div>

      {/* Header - Line 2 */}
      <div className="flex items-center gap-2 text-muted-foreground">
        {block.station && <span className="font-medium">{block.station}</span>}
        {block.station && <span>·</span>}
        <span>
          {formatTimeWithPreference(block.shiftStart, timeFormat)} – {formatTimeWithPreference(block.shiftEnd, timeFormat)}
        </span>
      </div>

      {/* Stats Bar */}
      <BlockStatsBar
        durationMins={durationMins}
        actualMins={block.actualMinutes}
        miles={miles}
        mileageSource={mileageSource}
        stops={block.stops}
        locations={block.locations}
        packages={block.packages}
        driveTimeMs={
          shift?.endedAt && shift?.startedAt
            ? shift.endedAt - shift.startedAt - (shift.pauseDuration || 0)
            : undefined
        }
      />

      {/* Overbooked Card - only show if route wasn't tracked */}
      {!shift && (
        block.overbooked ? (
          <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="flex items-center gap-3 py-4">
              <PartyPopper className="h-6 w-6 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-700 dark:text-amber-500">
                  Overbooked!
                </p>
                <p className="text-sm text-amber-600/80 dark:text-amber-400/80">
                  Enjoy the free money
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                onClick={() => updateOverbooked({ blockId, overbooked: false })}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => updateOverbooked({ blockId, overbooked: true })}
          >
            <Banknote className="h-4 w-4 mr-2" />
            Mark as Overbooked
          </Button>
        )
      )}

        {/* Earnings Card */}
        <BlockEarningsCard
          basePay={block.basePay}
          tip={block.tip}
          noRoutePayout={block.noRoutePayout}
          hourlyRate={hourlyRate}
          actualHourlyRate={actualHourlyRate}
          perMileRate={perMileRate}
          fuelEstimate={fuelEstimate}
        />

        {/* Route Section */}
        <Separator />
        <RouteSection
          shiftId={shift?._id}
          shiftStatus={shift?.status}
          screenshotUrl={screenshotUrl}
          hidden={showEditSheet}
        />

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => navigate(`/share/${blockId}`)}
          >
            <Share2 className="h-4 w-4 mr-1" />
            Share
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleEmailSupport}
          >
            <Mail className="h-4 w-4 mr-1" />
            Email
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => setShowEditSheet(true)}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="flex-1"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>

        {/* Notes */}
        {block.notes && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Notes</span>
              </div>
              <p className="pl-7 text-sm text-muted-foreground">{block.notes}</p>
            </div>
          </>
        )}

        {/* Issue Photos */}
        {blockId && (
          <>
            <Separator />
            <IssuePhotoGallery blockId={blockId} />
          </>
        )}

      <DeleteDialog
        blockId={blockId!}
        blockDate={formattedDate}
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) navigate("/blocks");
        }}
      />

      <BlockFormSheet
        mode="edit"
        blockId={blockId}
        trigger={<span />}
        open={showEditSheet}
        onOpenChange={setShowEditSheet}
      />
    </div>
  );
}
